import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Endpoints } from 'app/core/constants/Endpoints';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';

interface BaseResponse {
  status?: boolean;
  message?: string;
  exception?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PushTokenSyncService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _baseURL = environment.apiBaseURL;

  private readonly _fcmTokenKey = 'fcm_token';
  private readonly _authTokenKey = 'auth';
  private readonly _deviceIdKey = 'device_id';
  private readonly _syncedTokenKey = 'fcm_token_synced_value';
  private readonly _syncedDeviceKey = 'fcm_token_synced_device';

  async syncCachedTokenIfNeeded(force: boolean = false): Promise<boolean> {
    const authToken = await this._getPreference(this._authTokenKey);
    if (!authToken) {
      return false;
    }

    const [fcmToken, deviceId, lastSyncedToken, lastSyncedDevice] = await Promise.all([
      this._getPreference(this._fcmTokenKey),
      this._getPreference(this._deviceIdKey),
      this._getPreference(this._syncedTokenKey),
      this._getPreference(this._syncedDeviceKey)
    ]);

    if (!fcmToken || !deviceId) {
      return false;
    }

    if (!force && fcmToken === lastSyncedToken && deviceId === lastSyncedDevice) {
      return false;
    }

    try {
      const response = await firstValueFrom(
        this._httpClient.post<BaseResponse>(
          `${this._baseURL}${Endpoints.notifications.syncDeviceToken}`,
          { fcmToken }
        )
      );

      if (response?.status) {
        await Promise.all([
          Preferences.set({ key: this._syncedTokenKey, value: fcmToken }),
          Preferences.set({ key: this._syncedDeviceKey, value: deviceId })
        ]);
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Failed to sync push token with backend.', error);
      return false;
    }
  }

  async deactivateCurrentTokenIfNeeded(authTokenOverride?: string | null): Promise<boolean> {
    // Snapshot the auth token BEFORE any logout cleanup runs. AuthService.signOut()
    // clears the 'auth' preference right after calling this, and the HTTP interceptor
    // reads the token at request time — if it's already gone the request goes out
    // without an Authorization header and the backend answers 401 Unauthorized.
    const authToken = authTokenOverride ?? (await this._getPreference(this._authTokenKey));
    const [fcmToken, deviceId] = await Promise.all([
      this._getPreference(this._fcmTokenKey),
      this._getPreference(this._deviceIdKey)
    ]);

    if (!authToken || !deviceId) {
      await this.clearSyncMetadata();
      return false;
    }

    try {
      const response = await firstValueFrom(
        this._httpClient.post<BaseResponse>(
          `${this._baseURL}${Endpoints.notifications.deactivateDeviceToken}`,
          { fcmToken: fcmToken || null },
          // Explicit header so the request never depends on storage state mid-logout.
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              'X-Auth-Type': 'AUTH',
              'X-App-Type': 'LUNA_ADMIN'
            }
          }
        )
      );

      await this.clearSyncMetadata();
      return Boolean(response?.status);
    } catch (error) {
      // 401/403 here simply means the session was already invalid — the deactivation
      // is best-effort and must never block or break the logout flow.
      console.warn('Failed to deactivate push token on backend.', error);
      await this.clearSyncMetadata();
      return false;
    }
  }

  async clearSyncMetadata(): Promise<void> {
    await Promise.all([
      Preferences.remove({ key: this._syncedTokenKey }),
      Preferences.remove({ key: this._syncedDeviceKey })
    ]);
  }

  private async _getPreference(key: string): Promise<string | null> {
    const { value } = await Preferences.get({ key });
    if (!value || value === 'null' || value === 'undefined') {
      return null;
    }

    return value;
  }
}